import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen, faSpinner, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

function QuickSavePage() {
  const [status, setStatus] = useState('saving');
  const [message, setMessage] = useState('Saving to Tickler...');
  const location = useLocation();

  useEffect(() => {
    const saveContent = async () => {
      try {
        // Parse URL parameters
        const params = new URLSearchParams(location.search);
        const url = params.get('url');
        const title = params.get('title');
        const email = params.get('email');
        const source = params.get('source');

        if (!url || !email) {
          setStatus('error');
          setMessage('Missing required parameters');
          return;
        }

        // Prepare data based on source
        const data = {
          From: email,
          Subject: title || 'Untitled',
          'stripped-text': '',
          url: url,
          tickers: '',
          'X-Tickler-Source': 'bookmarklet'
        };

        // Add twitter_url if this is a Twitter save
        if (source === 'twitter') {
          data.twitter_url = url;
        }

        // Make API call from OUR domain (no CSP restrictions)
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${apiUrl}/api/inbound-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tickler-Source': 'bookmarklet'
          },
          body: JSON.stringify(data)
        });

        if (response.ok) {
          setStatus('success');
          setMessage('Successfully saved to Tickler!');
          
          // Auto-close after 2 seconds
          setTimeout(() => {
            window.close();
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Failed to save. Please try again.');
        }
      } catch (error) {
        console.error('Save error:', error);
        setStatus('error');
        setMessage(`Error: ${error.message}`);
      }
    };

    saveContent();
  }, [location]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '48px 40px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        textAlign: 'center',
        maxWidth: '420px',
        minWidth: '360px'
      }}>
        {/* Logo/Icon */}
        <div style={{
          marginBottom: '24px'
        }}>
          <FontAwesomeIcon 
            icon={faFolderOpen} 
            style={{
              fontSize: '48px',
              color: '#667eea',
              marginBottom: '16px'
            }}
          />
        </div>

        {/* Status Icons */}
        {status === 'saving' && (
          <>
            <FontAwesomeIcon 
              icon={faSpinner} 
              spin
              style={{
                fontSize: '56px',
                color: '#667eea',
                marginBottom: '24px',
                display: 'block'
              }}
            />
          </>
        )}
        
        {status === 'success' && (
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: '#10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            animation: 'successPop 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}>
            <FontAwesomeIcon 
              icon={faCheck}
              style={{
                fontSize: '36px',
                color: 'white'
              }}
            />
          </div>
        )}
        
        {status === 'error' && (
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            animation: 'successPop 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}>
            <FontAwesomeIcon 
              icon={faTimes}
              style={{
                fontSize: '36px',
                color: 'white'
              }}
            />
          </div>
        )}
        
        <style>{`
          @keyframes successPop {
            0% { transform: scale(0); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }
        `}</style>

        {/* Status Text */}
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '12px',
          letterSpacing: '-0.025em'
        }}>
          {status === 'saving' && 'Saving to Tickler'}
          {status === 'success' && 'Successfully Saved!'}
          {status === 'error' && 'Save Failed'}
        </h2>
        
        <p style={{
          fontSize: '15px',
          color: '#6b7280',
          marginBottom: '24px',
          lineHeight: '1.6'
        }}>
          {message}
        </p>
        
        {status === 'success' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '13px',
            color: '#9ca3af',
            marginTop: '16px'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#10b981',
              animation: 'pulse 1.5s ease-in-out infinite'
            }} />
            Closing automatically...
          </div>
        )}
        
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
        
        {status === 'error' && (
          <button
            onClick={() => window.close()}
            style={{
              padding: '12px 32px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              boxShadow: '0 4px 6px rgba(102, 126, 234, 0.25)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              marginTop: '8px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 12px rgba(102, 126, 234, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 6px rgba(102, 126, 234, 0.25)';
            }}
          >
            Close Window
          </button>
        )}
      </div>
    </div>
  );
}

export default QuickSavePage;
